import { usePostHog } from '@posthog/react';
import IconButton from 'components/@extended/IconButton';
import { buttonStyle } from 'components/buttons/buttonStyle';
import EmptyState from 'components/EmptyState';
import { Close as CloseIcon, NotificationsNoneOutlined as NotificationsNoneOutlinedIcon } from 'components/icons';
import TabsGallery, { TabType } from 'components/TabsGallery';
import { Button } from 'components/ui/button';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { dispatch, useSelector } from 'store';
import {
    useArchiveNotificationMutation,
    useArchiveNotificationsMutation,
    useReadNotificationMutation,
    useReadNotificationsMutation,
} from 'store/api/notifications';
import { FeatureFlag } from 'types/features';
import { AutomatedNotification, NotificationCategory } from 'types/notifications';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

import CountBox from 'layout/MainLayout/Header/CountBox';
import { closeTemporaryDrawer } from 'store/reducers/temporaryDrawer';
import NotificationItem from './NotificationItem';

interface Props {
    notifications: AutomatedNotification[];
}

interface PropsNotificationList extends Props {
    unread: number;
    /** Tab label, interpolated into the empty-state body ("{category} notifications will appear here."). */
    categoryLabel: string;
    /** Stable, unlocalised tab id for analytics — `categoryLabel` is translated, so the same tab
     *  would otherwise report a different `surface` per locale. */
    categoryId: string;
    handleRead: (id: string | number | 'all', isMarkingRead?: boolean) => Promise<void>;
    handleArchive: (id: string | number | 'all') => Promise<void>;
    areButtonsDisabled: boolean;
}

const CATEGORY_BY_TAB_ID: Record<string, NotificationCategory> = {
    invoicing: NotificationCategory.INVOICING,
    'cases-and-clients': NotificationCategory.CASE_AND_CLIENT,
    correspondence: NotificationCategory.CORRESPONDENCE,
    deadlines: NotificationCategory.DEADLINES,
    tasks: NotificationCategory.TASKS,
    compliance: NotificationCategory.COMPLIANCE,
    drafts: NotificationCategory.DRAFTS,
};

const DrawerContent: React.FC<Props> = ({ notifications }) => {
    const posthog = usePostHog();
    const [readNotification] = useReadNotificationMutation();
    const [archiveNotification] = useArchiveNotificationMutation();
    const [readNotifications] = useReadNotificationsMutation();
    const [archiveNotifications] = useArchiveNotificationsMutation();

    const { buttonsDisabled: areButtonsDisabled } = useSelector((state) => state.buttons);
    const isCorrespondenceEnabled = useIsFeatureEnabled(FeatureFlag.CORRESPONDENCE_MODULE);

    const [
        caseAndClientNotifications,
        invoicingNotifications,
        correspondenceNotifications,
        deadlineNotifications,
        tasksNotifications,
        complianceNotifications,
        draftsNotifications,
    ] = useMemo(() => {
        const caseAndClient: AutomatedNotification[] = [];
        const invoicing: AutomatedNotification[] = [];
        const correspondence: AutomatedNotification[] = [];
        const deadlines: AutomatedNotification[] = [];
        const tasks: AutomatedNotification[] = [];
        const compliance: AutomatedNotification[] = [];
        const drafts: AutomatedNotification[] = [];

        notifications.forEach((val) => {
            switch (val.category) {
                case NotificationCategory.CASE_AND_CLIENT:
                    caseAndClient.push(val);
                    break;
                case NotificationCategory.INVOICING:
                    invoicing.push(val);
                    break;
                case NotificationCategory.CORRESPONDENCE:
                    correspondence.push(val);
                    break;
                case NotificationCategory.DEADLINES:
                    deadlines.push(val);
                    break;
                case NotificationCategory.TASKS:
                    tasks.push(val);
                    break;
                case NotificationCategory.COMPLIANCE:
                    compliance.push(val);
                    break;
                case NotificationCategory.DRAFTS:
                    drafts.push(val);
                    break;
                default:
                    break;
            }
        });

        return [caseAndClient, invoicing, correspondence, deadlines, tasks, compliance, drafts];
    }, [notifications]);

    const handleRead = useCallback(
        async (id: string | number | 'all', type: NotificationCategory, isMarkingRead = true): Promise<void> => {
            if (id === 'all') {
                await readNotifications({ type })
                    .unwrap()
                    .then(() => {
                        posthog.capture('notifications_marked_read', { category: type, scope: 'all' });
                    })
                    .catch(console.error);
            } else if (id && !isNaN(Number(id))) {
                await readNotification({ id })
                    .unwrap()
                    .then(() => {
                        if (isMarkingRead) {
                            posthog.capture('notifications_marked_read', { category: type, scope: 'one' });
                        }
                    })
                    .catch(console.error);
            }
        },
        [readNotification, readNotifications, posthog]
    );

    const handleArchive = useCallback(
        async (id: string | number | 'all', type: NotificationCategory): Promise<void> => {
            if (id === 'all') {
                await archiveNotifications({ type })
                    .unwrap()
                    .then(() => {
                        posthog.capture('notification_archived', { category: type, scope: 'all' });
                    })
                    .catch(console.error);
            } else if (id && !isNaN(Number(id))) {
                await archiveNotification({ id })
                    .unwrap()
                    .then(() => {
                        posthog.capture('notification_archived', { category: type, scope: 'one' });
                    })
                    .catch(console.error);
            }
        },
        [archiveNotification, archiveNotifications, posthog]
    );

    const TABS: TabType[] = useMemo(() => {
        const caseAndClientUnreadNotificationsCount =
            caseAndClientNotifications?.filter((val) => !val.readAt).length ?? 0;
        const invoicinUnreadNotificationsCount = invoicingNotifications?.filter((val) => !val.readAt).length ?? 0;
        const correspondenceUnreadNotificationsCount =
            correspondenceNotifications?.filter((val) => !val.readAt).length ?? 0;
        const deadlineUnreadNotificationsCount = deadlineNotifications?.filter((val) => !val.readAt).length ?? 0;
        const tasksUnreadNotificationsCount = tasksNotifications?.filter((val) => !val.readAt).length ?? 0;
        const complianceUnreadNotificationsCount = complianceNotifications?.filter((val) => !val.readAt).length ?? 0;
        const draftsUnreadNotificationsCount = draftsNotifications?.filter((val) => !val.readAt).length ?? 0;
        return [
            {
                id: 'invoicing',
                label: t('notifications.invoicing'),
                component: (
                    <NotificationList
                        notifications={invoicingNotifications}
                        unread={invoicinUnreadNotificationsCount}
                        handleRead={(id, isMarkingRead) =>
                            handleRead(id, NotificationCategory.INVOICING, isMarkingRead)
                        }
                        handleArchive={(id) => handleArchive(id, NotificationCategory.INVOICING)}
                        areButtonsDisabled={areButtonsDisabled}
                        categoryLabel={t('notifications.invoicing')}
                        categoryId="invoicing"
                    />
                ),
                className: 'h-full overflow-auto',
                icon: <CountBox>{invoicinUnreadNotificationsCount}</CountBox>,
            },
            {
                id: 'cases-and-clients',
                label: t('notifications.cases-and-clients'),
                component: (
                    <NotificationList
                        notifications={caseAndClientNotifications}
                        unread={caseAndClientUnreadNotificationsCount}
                        handleRead={(id, isMarkingRead) =>
                            handleRead(id, NotificationCategory.CASE_AND_CLIENT, isMarkingRead)
                        }
                        handleArchive={(id) => handleArchive(id, NotificationCategory.CASE_AND_CLIENT)}
                        areButtonsDisabled={areButtonsDisabled}
                        categoryLabel={t('notifications.cases-and-clients')}
                        categoryId="cases-and-clients"
                    />
                ),
                className: 'h-full overflow-auto',
                icon: <CountBox>{caseAndClientUnreadNotificationsCount}</CountBox>,
            },
            ...(isCorrespondenceEnabled
                ? [
                      {
                          id: 'correspondence',
                          label: t('notifications.correspondence'),
                          component: (
                              <NotificationList
                                  notifications={correspondenceNotifications}
                                  unread={correspondenceUnreadNotificationsCount}
                                  handleRead={(id, isMarkingRead) =>
                                      handleRead(id, NotificationCategory.CORRESPONDENCE, isMarkingRead)
                                  }
                                  handleArchive={(id) => handleArchive(id, NotificationCategory.CORRESPONDENCE)}
                                  areButtonsDisabled={areButtonsDisabled}
                                  categoryLabel={t('notifications.correspondence')}
                                  categoryId="correspondence"
                              />
                          ),
                          className: 'h-full overflow-auto',
                          icon: <CountBox>{correspondenceUnreadNotificationsCount}</CountBox>,
                      },
                  ]
                : []),
            {
                id: 'deadlines',
                label: t('notifications.deadlines'),
                component: (
                    <NotificationList
                        notifications={deadlineNotifications}
                        unread={deadlineUnreadNotificationsCount}
                        handleRead={(id, isMarkingRead) =>
                            handleRead(id, NotificationCategory.DEADLINES, isMarkingRead)
                        }
                        handleArchive={(id) => handleArchive(id, NotificationCategory.DEADLINES)}
                        areButtonsDisabled={areButtonsDisabled}
                        categoryLabel={t('notifications.deadlines')}
                        categoryId="deadlines"
                    />
                ),
                className: 'h-full overflow-auto',
                icon: <CountBox>{deadlineUnreadNotificationsCount}</CountBox>,
            },
            {
                id: 'tasks',
                label: t('notifications.tasks'),
                component: (
                    <NotificationList
                        notifications={tasksNotifications}
                        unread={tasksUnreadNotificationsCount}
                        handleRead={(id, isMarkingRead) => handleRead(id, NotificationCategory.TASKS, isMarkingRead)}
                        handleArchive={(id) => handleArchive(id, NotificationCategory.TASKS)}
                        areButtonsDisabled={areButtonsDisabled}
                        categoryLabel={t('notifications.tasks')}
                        categoryId="tasks"
                    />
                ),
                className: 'h-full overflow-auto',
                icon: <CountBox>{tasksUnreadNotificationsCount}</CountBox>,
            },
            {
                id: 'compliance',
                label: t('notifications.compliance'),
                component: (
                    <NotificationList
                        notifications={complianceNotifications}
                        unread={complianceUnreadNotificationsCount}
                        handleRead={(id, isMarkingRead) =>
                            handleRead(id, NotificationCategory.COMPLIANCE, isMarkingRead)
                        }
                        handleArchive={(id) => handleArchive(id, NotificationCategory.COMPLIANCE)}
                        areButtonsDisabled={areButtonsDisabled}
                        categoryLabel={t('notifications.compliance')}
                        categoryId="compliance"
                    />
                ),
                className: 'h-full overflow-auto',
                icon: <CountBox>{complianceUnreadNotificationsCount}</CountBox>,
            },
            {
                id: 'drafts',
                label: t('notifications.drafts'),
                component: (
                    <NotificationList
                        notifications={draftsNotifications}
                        unread={draftsUnreadNotificationsCount}
                        handleRead={(id, isMarkingRead) => handleRead(id, NotificationCategory.DRAFTS, isMarkingRead)}
                        handleArchive={(id) => handleArchive(id, NotificationCategory.DRAFTS)}
                        areButtonsDisabled={areButtonsDisabled}
                        categoryLabel={t('notifications.drafts')}
                        categoryId="drafts"
                    />
                ),
                className: 'h-full overflow-auto',
                icon: <CountBox>{draftsUnreadNotificationsCount}</CountBox>,
            },
        ];
    }, [
        caseAndClientNotifications,
        invoicingNotifications,
        correspondenceNotifications,
        deadlineNotifications,
        tasksNotifications,
        complianceNotifications,
        draftsNotifications,
        handleArchive,
        handleRead,
        areButtonsDisabled,
        isCorrespondenceEnabled,
    ]);

    const closeDrawer = useCallback(() => {
        dispatch(closeTemporaryDrawer());
    }, []);

    return (
        <div className="flex flex-col overflow-hidden">
            <div className="flex justify-between px-6 pt-5 pb-1">
                <p className="text-base leading-5.5 font-semibold">
                    <FormattedMessage id="notifications.notifications" />
                </p>
                <IconButton
                    aria-label="close notifications"
                    className="text-fg-secondary hover:text-fg"
                    onClick={closeDrawer}
                >
                    <CloseIcon />
                </IconButton>
            </div>

            <TabsGallery
                wrapperClassName="flex h-full flex-col overflow-hidden"
                className="mb-0 px-2"
                tabs={TABS}
                onTabChange={(tabId) => {
                    posthog.capture('notification_tab_switched', { category: CATEGORY_BY_TAB_ID[tabId] });
                }}
            />
        </div>
    );
};

const NotificationList: React.FC<PropsNotificationList> = ({
    notifications,
    unread,
    handleArchive,
    handleRead,
    areButtonsDisabled,
    categoryLabel,
    categoryId,
}) => {
    return (
        <ul>
            <UnreadListHeader
                unread={unread}
                onReadAll={() => {
                    void handleRead('all');
                }}
                onArchiveAll={() => {
                    void handleArchive('all');
                }}
                disabled={areButtonsDisabled}
            />
            {notifications.length === 0 ? (
                // The header ("0 unread", Mark all read, Archive all) used to sit over an empty tab
                // body, so a caught-up tab was indistinguishable from one still loading.
                <li>
                    <EmptyState
                        size="panel"
                        icon={NotificationsNoneOutlinedIcon}
                        titleId="notifications.empty.title"
                        bodyId="notifications.empty.body"
                        bodyValues={{ category: categoryLabel }}
                        surface={`notifications-${categoryId}`}
                    />
                </li>
            ) : null}
            {notifications.map((val) => (
                <NotificationItem
                    key={`notification-${val.id}`}
                    notification={val}
                    handleArchive={handleArchive}
                    handleRead={handleRead}
                    areButtonsDisabled={areButtonsDisabled}
                />
            ))}
        </ul>
    );
};

const HEADER_ACTION_CLASS = 'h-[22px] border-0 px-0 text-[13px] font-normal';

/** The grey row over a notification list: the unread count, and the two actions on the whole list. */
export const UnreadListHeader = ({
    unread,
    onReadAll,
    onArchiveAll,
    disabled,
}: {
    unread: number;
    onReadAll: () => void;
    onArchiveAll: () => void;
    disabled: boolean;
}) => (
    <li className="border-divider bg-grey-50 flex w-full justify-between border-b px-6 py-2.25">
        <p className="font-semibold">
            <FormattedMessage values={{ count: unread }} id="notifications.unread" />
        </p>
        <div className="flex gap-4">
            <Button {...buttonStyle({ variant: 'text' }, HEADER_ACTION_CLASS)} onClick={onReadAll} disabled={disabled}>
                <FormattedMessage id="notifications.mark-all-as-read" />
            </Button>
            <Button
                {...buttonStyle({ variant: 'text' }, HEADER_ACTION_CLASS)}
                onClick={onArchiveAll}
                disabled={disabled}
            >
                <FormattedMessage id="notifications.archive-all" />
            </Button>
        </div>
    </li>
);

export default DrawerContent;
